import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaSearch } from "react-icons/fa";
import { FaHeartCircleMinus, FaRegEye } from "react-icons/fa6";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import { TbHeartPlus } from "react-icons/tb";
import Swal from "sweetalert2";
import Loader from "../../../../assets/img/extras/HeartLoader.gif";

const IncomingCallSummary = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoader, setActionLoader] = useState({
    loading: false,
    index: null,
  });

  const [filters, setFilters] = useState({
    dateType: "today", // today | yesterday | weekly | monthly | custom
    type: "sum", // sum | average
    from: "",
    to: "",
    sortKey: "incomingCalls", // incomingCalls | callDuration
    sortOrder: "desc", // asc | desc
    limit: 10,
  });

  /* ================= FETCH ================= */
  const fetchSummary = async (initial = true) => {
    try {
      if (initial) setIsLoading(true);

      const params = {
        page,
        limit: filters.limit,
        dateType: filters.dateType,
        type: filters.type,
        sortKey: filters.sortKey,
        sortOrder: filters.sortOrder,
      };

      if (filters.dateType === "custom") {
        params.from = filters.from;
        params.to = filters.to;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/call/incoming-call-summary`,
        { params }
      );

      setData(res.data.data || []);
      setTotalPages(Math.ceil(res.data.totalUsers / filters.limit));
    } catch (err) {
      console.error("Error fetching incoming call summary:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateHeartVerification = (isHeartVerified, userId, index) => {
    setActionLoader(() => ({
      loading: true,
      index: index,
    }));
    let config = {
      method: "put",
      url: `${process.env.REACT_APP_API_URL}/users/heart-verification/${userId}`,
      data: {
        isHeartVerified: isHeartVerified,
      },
    };

    axios
      .request(config)
      .then((response) => {
        // console.log("Response of updateHeartVerification: ", response.data);
        fetchSummary(false);
        Swal.fire({
          icon: "success",

          title: `User Heart Verification`,
          text: `User ${
            isHeartVerified ? "marked" : "unmarked"
          } as heart-verified successfully`,
          timer: 2000,
          showConfirmButton: true,
          confirmButtonColor: "#a10f5b",
        });
      })
      .catch((error) => {
        console.log("Error in updateHeartVerification: ", error);
      })
      .finally(() => {
        setActionLoader(() => ({
          loading: false,
          index: null,
        }));
      });
  };

  useEffect(() => {
    fetchSummary();
  }, [page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    fetchSummary();
  };

  /* ================= UI ================= */
  return (
    <div className="p">
      {/* ================= FILTERS ================= */}
      <div className="mb-6 rounded-xl bg-white p-5 shadow-md">
        <h2 className="mb-4 text-2xl font-semibold">
          Incoming Call Summary (All Users)
        </h2>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {/* Date Type */}
          <div>
            <label className="mb-1 block font-medium">Duration</label>
            <select
              name="dateType"
              className="w-full rounded-md border p-2"
              value={filters.dateType}
              onChange={handleFilterChange}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="lastWeek">Last Week</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {/* Custom Dates */}
          {filters.dateType === "custom" && (
            <>
              <div>
                <label className="mb-1 block font-medium">From</label>
                <input
                  type="date"
                  name="from"
                  value={filters.from}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">To</label>
                <input
                  type="date"
                  name="to"
                  value={filters.to}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border p-2"
                />
              </div>
            </>
          )}

          {/* Calculation Type */}
          <div>
            <label className="mb-1 block font-medium">Calculation</label>
            <select
              name="type"
              className="w-full rounded-md border p-2"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="sum">Sum</option>
              <option value="average">Average (per day)</option>
            </select>
          </div>

          {/* Sort Key */}
          <div>
            <label className="mb-1 block font-medium">Sort By</label>
            <select
              name="sortKey"
              className="w-full rounded-md border p-2"
              value={filters.sortKey}
              onChange={handleFilterChange}
            >
              <option value="incomingCalls">Incoming Calls</option>
              <option value="callDuration">Call Duration</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <label className="mb-1 block font-medium">Sort Order</label>
            <select
              name="sortOrder"
              className="w-full rounded-md border p-2"
              value={filters.sortOrder}
              onChange={handleFilterChange}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        <button
          onClick={applyFilters}
          className="mt-4 flex items-center gap-2 rounded-lg bg-[#c51369] px-4 py-2 text-white shadow hover:bg-[#a10f5b]"
        >
          <FaSearch /> Apply Filters
        </button>
      </div>

      {/* ================= TABLE ================= */}
      <div className="overflow-x-auto rounded-t-2xl">
        {isLoading ? (
          <TableLoader />
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
              <tr>
                <th className="px-4 py-3 text-center">Sl No.</th>
                <th className="px-6 py-3 text-center">User Details</th>
                <th className="px-4 py-3 text-center">Incoming Calls</th>
                <th className="px-4 py-3 text-center">Call Duration (mins)</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {data?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-5 text-center text-gray-500">
                    No data found
                  </td>
                </tr>
              ) : (
                data?.map((item, index) => (
                  <tr
                    key={item.user._id}
                    className="bg-white text-gray-700 hover:bg-gray-50"
                  >
                    <td className="border px-4 py-4">
                      {(page - 1) * filters.limit + index + 1}
                    </td>

                    {/* USER DETAILS */}
                    <td className="border px-4 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={item.user.selected_Avatar}
                          alt="avatar"
                          className="h-10 w-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="font-bold text-navy-700">
                            {item.user.nickName}
                          </div>
                          <div>+{item.user.phone}</div>
                          <div className="text-sm text-gray-500">
                            {item.user.preferred_Language}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="border px-4 py-4 text-center">
                      {item.incomingCalls}
                    </td>

                    <td className="border px-4 py-4 text-center">
                      {item.callDurationMinutes}
                    </td>

                    <td className="border px-4 py-4 text-center">
                      {actionLoader?.loading && actionLoader.index === index ? (
                        <img src={Loader} alt="" className="h-8 w-8" />
                      ) : item.user?.isHeartVerified ? (
                        <FaHeartCircleMinus
                          color="#c51369"
                          size={20}
                          className="cursor-pointer hover:text-[#a10f5b]"
                          onClick={() =>
                            updateHeartVerification(
                              false,
                              item?.user._id,
                              index
                            )
                          }
                        />
                      ) : (
                        <TbHeartPlus
                          color="#c51369"
                          size={20}
                          className="cursor-pointer hover:text-[#a10f5b]"
                          onClick={() =>
                            updateHeartVerification(true, item?.user._id, index)
                          }
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* ================= PAGINATION ================= */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
        />
      </div>
    </div>
  );
};

export default IncomingCallSummary;
